import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Section } from "./shared";
import ActionButton from "./ActionButton";

const PriceCartSection = styled(Section)`
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  min-height: 424px;

  @media (max-width: 768px) {
    min-height: auto;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  gap: 16px;
`;

const VariantContainer = styled.div`
  margin-bottom: 16px;
`;

const VariantTitle = styled.p`
  opacity: 0.6;
  margin: 0 0 12px 0;
  font-size: 14px;
`;

const VariantsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const VariantCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 12px;
  background: ${({ selected }) => selected ? 'rgba(0, 247, 210, 0.15)' : '#0d1220'};
  border: 2px solid ${({ selected }) => selected ? '#00f7d2' : '#1f2937'};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  min-width: 100px;

  &:hover {
    ${({ disabled, selected }) => !disabled && !selected && `
      border-color: #5ce9ff;
      background: rgba(92, 233, 255, 0.1);
    `}
  }
`;

const ColorCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ color }) => {
    // Map color names to hex values
    const colorMap = {
      'red': '#ff0000',
      'green': '#00ff00',
      'blue': '#0000ff',
      'black': '#000000',
      'white': '#ffffff',
      'yellow': '#ffff00',
      'orange': '#ffa500',
      'purple': '#800080',
      'pink': '#ffc0cb',
      'brown': '#a52a2a',
      'gray': '#808080',
      'grey': '#808080',
    };
    return colorMap[color?.toLowerCase()] || color || '#9ca3af';
  }};
  border: 2px solid ${({ selected }) => selected ? '#00f7d2' : 'rgba(255, 255, 255, 0.2)'};
  box-shadow: ${({ selected }) => selected ? '0 0 12px rgba(0, 247, 210, 0.5)' : 'none'};
`;

const VariantInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const VariantColorName = styled.span`
  font-size: 12px;
  color: #e5e7eb;
  font-weight: 600;
  text-transform: capitalize;
`;

const VariantPrice = styled.span`
  font-size: 14px;
  color: #00f7d2;
  font-weight: 700;
`;

const VariantQty = styled.span`
  font-size: 11px;
  color: #9ca3af;
  opacity: 0.8;
`;

const ExpiryBadge = styled.span`
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
  margin-top: 4px;
  background: ${({ expired, expiringSoon }) => {
    if (expired) return 'rgba(239, 68, 68, 0.2)';
    if (expiringSoon) return 'rgba(251, 191, 36, 0.2)';
    return 'rgba(0, 247, 210, 0.15)';
  }};
  color: ${({ expired, expiringSoon }) => {
    if (expired) return '#ef4444';
    if (expiringSoon) return '#fbbf24';
    return '#00f7d2';
  }};
  border: 1px solid ${({ expired, expiringSoon }) => {
    if (expired) return '#ef4444';
    if (expiringSoon) return '#fbbf24';
    return '#00f7d2';
  }};
`;

const DateStatusBadge = styled.span`
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
  margin-top: 4px;
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
  border: 1px solid #fbbf24;
  text-align: center;
`;

function PriceCartPRO({ 
  price, 
  onAddToCart, 
  onBuyNow, 
  productData, 
  hasActiveAuction,
  hasActiveGroup,
  activeGroupId,
  showCustomOffer,
  onCustomOfferClick,
  showChatWithOwner,
  onChatWithOwnerClick,
  productName,
  showPurchaseButtons = true,
}) {
  const history = useHistory();
  const [qty, setQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Get variants from product_sizes_quantity with basic deduplication
  const variants = useMemo(() => {
    const rawVariants = productData?.product_sizes_quantity || [];
    const seenIds = new Set();
    const uniqueVariants = [];
    
    // Deduplicate by _id to avoid showing the same variant twice
    rawVariants.forEach((variant) => {
      if (variant._id) {
        if (!seenIds.has(variant._id)) {
          seenIds.add(variant._id);
          uniqueVariants.push(variant);
        }
      } else {
        // For variants without _id, include them (shouldn't happen normally)
        uniqueVariants.push(variant);
      }
    });
    
    return uniqueVariants;
  }, [productData?.product_sizes_quantity]);
  
  console.log("🚀 ~ PriceCartPRO ~ variants:", variants)

  const handleGroupPurchase = () => {
    if (activeGroupId) {
      history.push(`/groups/${activeGroupId}`);
    }
  };
  
  // Helper function to check if variant is available (moved before useEffects)
  const isVariantAvailable = (variant) => {
    const lotProduction = variant.lot_number && Array.isArray(variant.production)
      ? variant.production[0]
      : null;
    const effectiveQty = variant.lot_number
      ? (lotProduction?.quantity ?? variant.quantity ?? 0)
      : (variant.quantity ?? 0);
    
    if (effectiveQty <= 0) return false;
    
    // Check date status
    let startDateStr = null;
    let endDateStr = null;
    
    if (variant.lot_number && Array.isArray(variant.production) && variant.production.length > 0) {
      const production = variant.production[0];
      startDateStr = production?.start_date || production?.startDate;
      endDateStr = production?.expiry_date || production?.end_date || production?.expiryDate || production?.endDate;
    } else if (variant.isLotVariant && Array.isArray(variant.lot_details) && variant.lot_details.length > 0) {
      const productions = variant.lot_details.flatMap(lot => lot.production || []);
      if (productions.length > 0) {
        const startDates = productions
          .map(prod => prod.start_date || prod.startDate)
          .filter(Boolean)
          .map(dateStr => new Date(dateStr))
          .filter(date => !isNaN(date.getTime()));
        const endDates = productions
          .map(prod => prod.expiry_date || prod.end_date || prod.expiryDate || prod.endDate)
          .filter(Boolean)
          .map(dateStr => new Date(dateStr))
          .filter(date => !isNaN(date.getTime()));
        if (startDates.length > 0) {
          const earliestStart = new Date(Math.min(...startDates));
          startDateStr = earliestStart.toISOString();
        }
        if (endDates.length > 0) {
          const earliestEnd = new Date(Math.min(...endDates));
          endDateStr = earliestEnd.toISOString();
        }
      }
    }
    
    if (!startDateStr && !endDateStr) return true; // No date restrictions
    
    const now = new Date();
    const startDate = startDateStr ? new Date(startDateStr) : null;
    const endDate = endDateStr ? new Date(endDateStr) : null;
    
    if (startDate && !isNaN(startDate.getTime()) && now < startDate) return false;
    if (endDate && !isNaN(endDate.getTime()) && now > endDate) return false;
    
    return true;
  };

  // Set default variant on mount, but only if it's available
  useEffect(() => {
    if (variants.length > 0 && !selectedVariant) {
      const availableVariant = variants.find(variant => isVariantAvailable(variant));
      if (availableVariant) {
        setSelectedVariant(availableVariant);
      }
    }
  }, [variants, selectedVariant]);

  // Clear selection if selected variant becomes disabled
  useEffect(() => {
    if (selectedVariant && !isVariantAvailable(selectedVariant)) {
      setSelectedVariant(null);
    }
  }, [selectedVariant, variants]);
    // Helper function to check lot availability based on start and end dates
    const getLotDateStatus = (variant) => {
      if (!variant.lot_number && !variant.isLotVariant) return null;
  
      let startDateStr = null;
      let endDateStr = null;
  
      // Handle direct lot variants (with production array)
      if (variant.lot_number && Array.isArray(variant.production) && variant.production.length > 0) {
        const production = variant.production[0];
        startDateStr = production?.start_date || production?.startDate;
        endDateStr = production?.expiry_date || production?.end_date || production?.expiryDate || production?.endDate;
      }
      // Handle combined lot variants (isLotVariant with lot_details)
      else if (variant.isLotVariant && Array.isArray(variant.lot_details) && variant.lot_details.length > 0) {
        const productions = variant.lot_details.flatMap(lot => lot.production || []);
        if (productions.length > 0) {
          // Find earliest start date and earliest end date
          const startDates = productions
            .map(prod => prod.start_date || prod.startDate)
            .filter(Boolean)
            .map(dateStr => new Date(dateStr))
            .filter(date => !isNaN(date.getTime()));
          
          const endDates = productions
            .map(prod => prod.expiry_date || prod.end_date || prod.expiryDate || prod.endDate)
            .filter(Boolean)
            .map(dateStr => new Date(dateStr))
            .filter(date => !isNaN(date.getTime()));
          
          if (startDates.length > 0) {
            const earliestStart = new Date(Math.min(...startDates));
            startDateStr = earliestStart.toISOString();
          }
          if (endDates.length > 0) {
            const earliestEnd = new Date(Math.min(...endDates));
            endDateStr = earliestEnd.toISOString();
          }
        }
      }
  
      if (!startDateStr && !endDateStr) return null;
  
      const now = new Date();
      const startDate = startDateStr ? new Date(startDateStr) : null;
      const endDate = endDateStr ? new Date(endDateStr) : null;
  
      let isAvailable = true;
      let statusMessage = null;
      let statusType = null; // 'not_started' or 'expired'
  
      if (startDate && !isNaN(startDate.getTime())) {
        if (now < startDate) {
          isAvailable = false;
          statusType = 'not_started';
          const timeUntilStart = startDate - now;
          const days = Math.floor(timeUntilStart / (1000 * 60 * 60 * 24));
          const hours = Math.floor((timeUntilStart % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((timeUntilStart % (1000 * 60 * 60)) / (1000 * 60));
          
          if (days > 0) {
            statusMessage = `Starts in ${days}d ${hours}h`;
          } else if (hours > 0) {
            statusMessage = `Starts in ${hours}h ${minutes}m`;
          } else {
            statusMessage = `Starts in ${minutes}m`;
          }
        }
      }
  
      if (endDate && !isNaN(endDate.getTime())) {
        if (now > endDate) {
          isAvailable = false;
          statusType = 'expired';
          statusMessage = 'Expired';
        }
      }
  
      return {
        isAvailable,
        statusMessage,
        statusType,
        startDate: startDateStr,
        endDate: endDateStr
      };
    };
  // Update price when variant changes
  const selectedLotProduction = selectedVariant?.lot_number && Array.isArray(selectedVariant.production)
    ? selectedVariant.production[0]
    : null;

  const selectedVariantPrice = selectedVariant?.lot_number
    ? (selectedLotProduction?.price ?? selectedVariant?.price ?? 0)
    : (selectedVariant?.price ?? 0);

  const selectedVariantQty = selectedVariant?.lot_number
    ? (selectedLotProduction?.quantity ?? selectedVariant?.quantity ?? 0)
    : (selectedVariant?.quantity ?? 0);

  const currentPrice = selectedVariantPrice || price || 0;
  const availableQty = selectedVariantQty || 0;
  const selectedVariantDateStatus = selectedVariant ? getLotDateStatus(selectedVariant) : null;
  const isSelectedVariantDateDisabled = selectedVariantDateStatus && !selectedVariantDateStatus.isAvailable;

  const handleVariantSelect = (variant) => {
    const lotProduction = variant.lot_number && Array.isArray(variant.production)
      ? variant.production[0]
      : null;
    const effectiveQty = variant.lot_number
      ? (lotProduction?.quantity ?? variant.quantity ?? 0)
      : (variant.quantity ?? 0);

    const dateStatus = getLotDateStatus(variant);
    const isDateDisabled = dateStatus && !dateStatus.isAvailable;

    if (!hasActiveAuction && effectiveQty > 0 && !isDateDisabled) {
      setSelectedVariant(variant);
      // Reset quantity if it exceeds available quantity
      if (qty > effectiveQty) {
        setQty(effectiveQty);
      }
    }
  };

  const handleAddToCartWithVariant = () => {
    if (selectedVariant) {
      onAddToCart(qty, selectedVariant);
    } else {
      onAddToCart(qty);
    }
  };

  const handleBuyNowWithVariant = () => {
    if (selectedVariant) {
      onBuyNow(qty, selectedVariant);
    } else {
      onBuyNow(qty);
    }
  };



  // Helper function to format expiry date and determine status
  const getExpiryInfo = (variant) => {
    let expiryDateStr = null;

    // Handle combined lot variants (isLotVariant with lot_details)
    if (variant.isLotVariant && Array.isArray(variant.lot_details) && variant.lot_details.length > 0) {
      // Find the earliest expiry date from all lots in the variant
      const expiryDates = variant.lot_details
        .flatMap(lot => lot.production || [])
        .map(prod => prod.expiry_date || prod.end_date || prod.expiryDate || prod.endDate)
        .filter(Boolean)
        .map(dateStr => new Date(dateStr))
        .filter(date => !isNaN(date.getTime()));
      
      if (expiryDates.length > 0) {
        const earliestExpiry = new Date(Math.min(...expiryDates));
        expiryDateStr = earliestExpiry.toISOString();
      }
    }
    // Handle direct lot variants (with production array)
    else if (variant.lot_number && Array.isArray(variant.production) && variant.production.length > 0) {
      expiryDateStr = variant.production[0]?.expiry_date || variant.production[0]?.end_date || variant.production[0]?.expiryDate || variant.production[0]?.endDate;
    }

    if (!expiryDateStr) return null;

    const expiryDate = new Date(expiryDateStr);
    if (isNaN(expiryDate.getTime())) return null;

    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    const formattedDate = expiryDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    return {
      formattedDate,
      expired: daysUntilExpiry < 0,
      expiringSoon: daysUntilExpiry >= 0 && daysUntilExpiry <= 30,
      daysUntilExpiry
    };
  };

  return (
    <PriceCartSection>
      <h3 style={{ margin: '0 0 16px 0' }}>{productName || 'Purchase'}</h3>
      <ContentWrapper>
        {variants.length > 0 && (
          <VariantContainer>
            <VariantTitle>Select Variant</VariantTitle>
            <VariantsGrid>
              {variants.map((variant) => {
                const isSelected = selectedVariant?._id === variant._id;
                const lotProduction = variant.lot_number && Array.isArray(variant.production)
                  ? variant.production[0]
                  : null;

                const effectiveQty = variant.lot_number
                  ? (lotProduction?.quantity ?? variant.quantity ?? 0)
                  : (variant.quantity ?? 0);

                const effectivePrice = variant.lot_number
                  ? (lotProduction?.price ?? variant.price ?? 0)
                  : (variant.price ?? 0);

                const isOutOfStock = effectiveQty <= 0;
                const expiryInfo = getExpiryInfo(variant);
                const dateStatus = getLotDateStatus(variant);
                const isDateDisabled = dateStatus && !dateStatus.isAvailable;
                const isDisabled = hasActiveAuction || isOutOfStock || isDateDisabled;
                
                return (
                  <VariantCard
                    key={variant._id}
                    selected={isSelected}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleVariantSelect(variant)}
                  >
                    <ColorCircle 
                      color={variant.color} 
                      selected={isSelected}
                    />
                    <VariantInfo>
                      <VariantColorName>
                        {variant.lot_number
                          ? `Lot #${variant.lot_number}`
                          : (variant.color || "Variant")}
                      </VariantColorName>
                      <VariantPrice>${(effectivePrice || 0).toFixed(2)}</VariantPrice>
                      <VariantQty>
                        {isOutOfStock ? 'Out of Stock' : `Qty: ${effectiveQty}`}
                      </VariantQty>
                      {dateStatus && !dateStatus.isAvailable && (
                        <DateStatusBadge>
                          {dateStatus.statusMessage}
                        </DateStatusBadge>
                      )}
                      {!dateStatus && expiryInfo && (variant.lot_number || variant.isLotVariant) && (
                        <ExpiryBadge
                          expired={expiryInfo.expired}
                          expiringSoon={expiryInfo.expiringSoon}
                        >
                          {expiryInfo.expired ? 'Expired' : `Exp: ${expiryInfo.formattedDate}`}
                        </ExpiryBadge>
                      )}
                    </VariantInfo>
                  </VariantCard>
                );
              })}
            </VariantsGrid>
          </VariantContainer>
        )}
        
        <div>
          <p style={{ opacity: 0.6, margin: '0 0 8px 0' }}>Unit Price</p>
          <h3 style={{ color: '#00f7d2', margin: '0 0 8px 0' }}>
            ${(currentPrice || 0).toFixed(2)}
          </h3>
          <p style={{ fontSize: 12, opacity: 0.6, margin: 0 }}>
            Shipping Fee: {productData?.deliveryCharges || 0} {productData?.symbol || ''}
          </p>
          {selectedVariant && (
            <p style={{ fontSize: 12, opacity: 0.8, margin: '4px 0 0 0', color: '#9ca3af' }}>
              Available: {availableQty} units
            </p>
          )}
          {hasActiveAuction && (
            <p style={{ fontSize: 12, color: '#ff6b6b', margin: '8px 0 0 0', fontWeight: 600 }}>
              ⚠️ Active auction in progress - Normal purchase disabled
            </p>
          )}
          {hasActiveGroup && !hasActiveAuction && (
            <p style={{ fontSize: 12, color: '#8b5cf6', margin: '8px 0 0 0', fontWeight: 600 }}>
              ℹ️ This product is also available through Group Purchase
            </p>
          )}
        </div>
        
        <div style={{ 
          display: 'flex', 
          gap: 10, 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {showPurchaseButtons && (
            <input
              type='number'
              min={1}
              max={selectedVariant ? selectedVariant.quantity : undefined}
              value={qty}
              onChange={(e) => {
                const newQty = Math.max(1, parseInt(e.target.value) || 1);
                const maxQty = selectedVariant ? selectedVariant.quantity : Infinity;
                setQty(Math.min(newQty, maxQty));
              }}
              disabled={hasActiveAuction || !selectedVariant || availableQty === 0 || isSelectedVariantDateDisabled}
              style={{ 
                width: 60, 
                padding: 10, 
                borderRadius: 10, 
                background: (hasActiveAuction || isSelectedVariantDateDisabled) ? '#1f2937' : '#0d1220', 
                border: '1px solid #1f2937', 
                color: (hasActiveAuction || isSelectedVariantDateDisabled) ? '#777' : '#e5e7eb',
                cursor: (hasActiveAuction || isSelectedVariantDateDisabled) ? 'not-allowed' : 'text',
                opacity: (hasActiveAuction || isSelectedVariantDateDisabled) ? 0.5 : 1,
                flex: '0 0 auto'
              }}
            />
          )}
          {hasActiveGroup && !hasActiveAuction && (
            <ActionButton
              onClick={handleGroupPurchase}
              variant="contained"
              style={{ 
                background: '#8b5cf6',
                borderColor: '#8b5cf6',
                color: '#fff'
              }}
            >
              Group Purchase
            </ActionButton>
          )}
          {showPurchaseButtons && (
            <>
              <ActionButton
                onClick={handleBuyNowWithVariant}
                disabled={hasActiveAuction || !selectedVariant || availableQty === 0 || isSelectedVariantDateDisabled}
                variant="contained"
              >
                Buy Now
              </ActionButton>
              <ActionButton
                onClick={handleAddToCartWithVariant}
                disabled={hasActiveAuction || !selectedVariant || availableQty === 0 || isSelectedVariantDateDisabled}
                variant="outline"
              >
                Add to Cart
              </ActionButton>
            </>
          )}
          {showCustomOffer && (
            <ActionButton
              onClick={onCustomOfferClick}
              variant="outline"
            >
              Send Custom Offer
            </ActionButton>
          )}
          {showChatWithOwner && (
            <ActionButton
              onClick={onChatWithOwnerClick}
              variant="outline"
            >
              Chat with Owner
            </ActionButton>
          )}
        </div>
      </ContentWrapper>
    </PriceCartSection>
  );
}

export default PriceCartPRO;

